import { ReactNode } from 'react';
import { IconTransportation } from '../icon';
import { IconServicesAndAmenities } from '../icon';
import Link from '../link';
import { QuickLinkData } from './ItineraryBody';
import { FSLocale, localeLink } from '@fshr/shared';
import { useTranslations } from '@fshr/context/i18n-context';
import IconBeds from '../icon/beds';
import Button from '../button';
import { cn } from '@fshr/shared-react';

const UPCOMING_TRIP_PATH = '/upcoming-trip';
const MANAGE_STAY_LABEL = 'Manage stay';

type QuickLinksSectionProps = {
	quickLinks: QuickLinkData[];
	locale: FSLocale;
	closeComponent?: () => void;
	urlPath: string;
	buttonClassName?: string;
	linkClassName?: string;
};

type QuickLinkObject = {
	Icon: ReactNode;
};

const QUICK_LINKS_ICONS: Record<string, QuickLinkObject> = {
	Beds: {
		Icon: <IconBeds />,
	},
	Transportation: {
		Icon: <IconTransportation />,
	},
	ServicesAndAmenities: {
		Icon: <IconServicesAndAmenities />,
	},
};

/**
 * @description Component used to display quick link information
 *
 * @param { string } icon - Quick Link icon name
 * @param { string } label - Quick link label
 * @param { string } url - Redirect URL
 * @param { FSLocale } locale - Selected Language
 * @param { function(text:string, params?: (string | number)[]): string } - Translation function
 *
 * @returns { FC } Quick link component
 */
function QuickLink(
	props: QuickLinkData & {
		locale: FSLocale;
		closeComponent?: () => void;
		buttonClassName?: string;
		linkClassName?: string;
	}
) {
	const { icon, label, url, locale, closeComponent, buttonClassName, linkClassName } = props;

	const urlWithLocale = localeLink(locale)(url);
	const t = useTranslations();

	return (
		<div
			className="rounded-[64px] w-[80px] md:w-auto spacing-stack-xs flex flex-col items-center justify-center"
			role="listitem"
		>
			{closeComponent ? (
				<Button
					hasIcon={true}
					onClick={closeComponent}
					intent="unstyled"
					className={cn(
						'flex justify-center items-center cursor-pointer bg-surface-raised-subtle border-border-subtle rounded-[4px] w-[45px] max-h-[45px] md:w-[60px] md:max-h-[60px] border-[2px] padding-stack-s text-center',
						buttonClassName
					)}
					aria-label={t(label)}
					tabIndex={0}
				>
					{QUICK_LINKS_ICONS[`${icon}`].Icon}
				</Button>
			) : (
				<Link
					href={urlWithLocale}
					intent="unstyled"
					className={cn(
						'flex justify-center items-center cursor-pointer bg-surface-raised-subtle border-border-subtle rounded-[4px] w-[45px] max-h-[45px] md:w-[60px] md:max-h-[60px] border-[2px] padding-stack-s text-center',
						linkClassName
					)}
					aria-label={t(label)}
					tabIndex={0}
				>
					{QUICK_LINKS_ICONS[`${icon}`].Icon}
				</Link>
			)}
			<div
				className="max-w-[100px] text-label text-text-primary text-center"
				style={{ fontSize: 10 }}
				aria-hidden={true}
			>
				{t(label)}
			</div>
		</div>
	);
}

const shouldShowCloseComponent = (url: string, label: string, urlPath: string): boolean => {
	const baseUrl = url.split('?')[0];
	return (
		baseUrl.replace(/\//g, '') === urlPath.replace(/\//g, '') ||
		(url.includes(UPCOMING_TRIP_PATH) && label === MANAGE_STAY_LABEL)
	);
};

export function QuickLinksSection(props: QuickLinksSectionProps) {
	const { quickLinks, locale, closeComponent, urlPath, linkClassName, buttonClassName } = props;
	const t = useTranslations();

	return (
		<section
			className="w-full border-b-[1px] border-b-border-subtle md:pt-[15px]"
			aria-label={t('Quick Links Section')}
		>
			<div
				className="flex flex-row py-150 px-150 md:py-[20px] md:px-[30px] spacing-inline-xs items-start"
				role="list"
				aria-label={t('Quick links navigation')}
			>
				{quickLinks.map(({ icon, label, url }, index) => (
					<QuickLink
						locale={locale}
						url={url}
						closeComponent={
							shouldShowCloseComponent(url, label, urlPath) ? closeComponent : undefined
						}
						icon={icon}
						label={label}
						key={`quick-link-section-${index}-${label}`}
						linkClassName={linkClassName}
						buttonClassName={buttonClassName}
					/>
				))}
			</div>
		</section>
	);
}
